import { z } from 'zod'
import {
  ROLES,
  TENANT_PLANS,
  TENANT_STATUS,
  RECOMMENDATION_TYPE,
  RECOMMENDATION_SEVERITY,
  RECOMMENDATION_STATUS,
  AI_RECOMMENDATION_CATEGORY,
  AI_RECOMMENDATION_VISIBILITY,
  AI_CONTEXT_SCOPE,
  AI_CONTEXT_CATEGORY,
  AI_TENANT_VISIBILITY_MODE,
  AI_TENANT_STRATEGY,
  AI_GENERATION_FREQUENCY,
  AI_CONTEXT_MAX_LENGTH,
  EMAIL_SENDING_DOMAIN,
  MAX_NOTIFICATION_RECIPIENTS,
} from '../constants/index'
import type { RecommendationType } from '../constants/index'

// ─── Safe text (anti-XSS) ───────────────────────────────────

// SEC (H-01): los campos de texto libre (name, displayName, organizationName,
// description, etc.) se devuelven en respuestas JSON consumidas por el SPA
// que los renderiza en distintas pantallas. Aunque React escapa por defecto,
// algunos lugares hacen interpolación directa (informes, exports). Para
// eliminar el vector de XSS persistente en el input, rechazamos cualquier
// carácter de marcado o de control:
//   - `< >`: HTML / scripts
//   - `{ }`: template injection
//   - `\\`: escapes / paths
//   - `\``: backticks (template strings)
//   - control chars (incluido CRLF) que permiten log injection
//
// `safeText(min, max)` devuelve un schema reutilizable. El trim final
// elimina espacios accidentales antes de persistir.
// Rechazamos control chars (CRLF/TAB/NUL/DEL) ademas de < > { } \ y
// backtick — son exactamente los vectores de XSS persistente, log
// injection y forjado de cabeceras. La regla no-control-regex avisa
// por defecto, pero aqui el match es intencional.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHAR_RE = /[<>{}\\`\u0000-\u001f\u007f]/
export function safeText(min: number, max: number, label = 'campo') {
  return z
    .string()
    .min(min, `${label}: mínimo ${min} caracteres`)
    .max(max, `${label}: máximo ${max} caracteres`)
    .refine((s) => !UNSAFE_CHAR_RE.test(s), {
      message: `${label}: contiene caracteres no permitidos (<, >, {, }, \\, \`, control)`,
    })
    .transform((s) => s.trim())
}

// ─── Auth ───────────────────────────────────────────────────

// SEC (H-08): política de contraseña única y reutilizada en todos los flujos
// de asignación/cambio de password (register, accept-invite, change-password,
// reset-password). Antes había validación inconsistente: register exigía
// mayúscula+dígito pero change-password y accept-invite solo longitud.
export const passwordPolicySchema = z
  .string()
  .min(8, 'Mínimo 8 caracteres')
  .max(128, 'Máximo 128 caracteres')
  .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
  .regex(/[a-z]/, 'Debe contener al menos una minúscula')
  .regex(/[0-9]/, 'Debe contener al menos un número')

export const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Contraseña requerida'),
})

// El endpoint POST /api/auth/register fue retirado (H-02). Conservamos el
// schema porque algunos tests / docs todavía lo referencian, pero ya no
// se usa en runtime.
export const registerSchema = z.object({
  email: z.string().email('Email inválido'),
  password: passwordPolicySchema,
  name: safeText(2, 100, 'Nombre'),
  organizationName: safeText(2, 200, 'Organización'),
})

export const mfaVerifySchema = z.object({
  tempToken: z.string(),
  code: z.string().length(6, 'El código debe tener 6 dígitos'),
})

export const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido'),
})

export const resetPasswordSchema = z.object({
  token: z.string(),
  password: passwordPolicySchema,
})

export const acceptInviteSchema = z.object({
  token: z.string().min(1, 'Token requerido'),
  password: passwordPolicySchema,
})

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Contraseña actual requerida'),
  newPassword: passwordPolicySchema,
})

// ─── Tenant ─────────────────────────────────────────────────

export const updateTenantSchema = z.object({
  name: safeText(2, 200, 'Nombre').optional(),
  plan: z.enum([TENANT_PLANS.TRIAL, TENANT_PLANS.STARTER, TENANT_PLANS.PRO, TENANT_PLANS.ENTERPRISE]).optional(),
  status: z.enum([TENANT_STATUS.ACTIVE, TENANT_STATUS.SUSPENDED, TENANT_STATUS.CANCELLED]).optional(),
  maxM365Tenants: z.number().int().min(1).optional(),
  maxUsers: z.number().int().min(1).optional(),
})

export const adminCreateTenantSchema = z.object({
  name: safeText(2, 200, 'Nombre'),
  plan: z.enum([TENANT_PLANS.TRIAL, TENANT_PLANS.STARTER, TENANT_PLANS.PRO, TENANT_PLANS.ENTERPRISE]).optional(),
  maxM365Tenants: z.number().int().min(1).max(1000).optional(),
  maxUsers: z.number().int().min(1).max(100_000).optional(),
  adminEmail: z.string().email('Email inválido'),
  adminName: safeText(2, 100, 'Nombre admin'),
  adminPassword: passwordPolicySchema,
})

export const updateTenantSettingsSchema = z.object({
  name: safeText(2, 200, 'Nombre').optional(),
  settingsJson: z.string().max(10_000).optional(),
})

// ─── Email notifications (/settings › Correo) ──────────────

const recommendationTypeEnum = z.enum(
  Object.values(RECOMMENDATION_TYPE) as [RecommendationType, ...RecommendationType[]],
)

// Editable por tenant_admin y tenant_manager.
export const updateNotificationSettingsSchema = z.object({
  recommendationsEnabled: z.boolean(),
  recipients: z
    .array(z.string().email('Email inválido').max(254).transform((e) => e.trim().toLowerCase()))
    .max(MAX_NOTIFICATION_RECIPIENTS, `Máximo ${MAX_NOTIFICATION_RECIPIENTS} destinatarios`)
    .refine((arr) => new Set(arr).size === arr.length, { message: 'Destinatarios duplicados' }),
  recommendationTypes: z
    .array(recommendationTypeEnum)
    .refine((arr) => new Set(arr).size === arr.length, { message: 'Tipos duplicados' }),
  minSeverity: z.enum([
    RECOMMENDATION_SEVERITY.LOW,
    RECOMMENDATION_SEVERITY.MEDIUM,
    RECOMMENDATION_SEVERITY.HIGH,
    RECOMMENDATION_SEVERITY.CRITICAL,
  ]),
  minMonthlySaving: z.number().min(0).max(1_000_000),
  notifyOnRecalculate: z.boolean(),
})

// Solo super_admin. El dominio del remitente es fijo (Cloudflare Email Service
// solo envía desde dominios dados de alta); el local-part y el nombre son libres.
export const updateNotificationFromSchema = z.object({
  fromName: safeText(1, 60, 'Nombre del remitente').refine((s) => !s.includes('"'), {
    message: 'Nombre del remitente: comillas no permitidas',
  }),
  fromAddress: z
    .string()
    .email('Email inválido')
    .max(254)
    .transform((e) => e.trim().toLowerCase())
    .refine((e) => e.endsWith(`@${EMAIL_SENDING_DOMAIN}`), {
      message: `El remitente debe pertenecer al dominio ${EMAIL_SENDING_DOMAIN}`,
    }),
})

// ─── Team ───────────────────────────────────────────────────

export const inviteUserSchema = z.object({
  email: z.string().email('Email inválido'),
  name: safeText(2, 100, 'Nombre'),
  role: z.enum([ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER, ROLES.TENANT_VIEWER]),
})

// ─── M365 Tenant (alta manual) ──────────────────────────────

export const m365TenantManualSchema = z.object({
  azureTenantId: z
    .string()
    .min(1, 'azureTenantId requerido')
    .max(64)
    .regex(/^[a-zA-Z0-9-]+$/, 'azureTenantId solo admite alfanuméricos y guiones'),
  displayName: safeText(1, 200, 'Display name'),
  domain: z
    .string()
    .max(253)
    .regex(/^[a-zA-Z0-9.-]+$/, 'Dominio inválido')
    .optional(),
  clientId: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z0-9-]+$/, 'clientId inválido'),
  clientSecret: z.string().min(1).max(512),
})

export const updateUserRoleSchema = z.object({
  role: z.enum([ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER, ROLES.TENANT_VIEWER]),
})

// ─── M365 Tenant ────────────────────────────────────────────

export const updateM365TenantSchema = z.object({
  syncEnabled: z.boolean().optional(),
})

// ─── License Pricing ────────────────────────────────────────

export const updateLicensePricingSchema = z.object({
  monthlyCostPerUnit: z.number().min(0).optional(),
  currency: z.string().length(3).optional(),
})

// ─── Recommendations ────────────────────────────────────────

export const dismissRecommendationSchema = z.object({
  reason: z.string().max(500).optional(),
})

// ─── Pagination ─────────────────────────────────────────────

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(50),
})

// ─── Filters ────────────────────────────────────────────────

export const userFiltersSchema = z.object({
  search: z.string().optional(),
  accountEnabled: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : undefined), z.boolean().optional()),
  hasLicense: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : undefined), z.boolean().optional()),
  isActive30d: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : undefined), z.boolean().optional()),
  department: z.string().optional(),
  skuPartNumber: z.string().optional(),
})

export const recommendationFiltersSchema = z.object({
  status: z
    .enum([
      RECOMMENDATION_STATUS.PENDING,
      RECOMMENDATION_STATUS.ACCEPTED,
      RECOMMENDATION_STATUS.DISMISSED,
      RECOMMENDATION_STATUS.APPLIED,
      RECOMMENDATION_STATUS.EXPIRED,
      RECOMMENDATION_STATUS.RESOLVED,
    ])
    .optional(),
  type: z
    .enum([
      RECOMMENDATION_TYPE.REMOVE_LICENSE,
      RECOMMENDATION_TYPE.DOWNGRADE,
      RECOMMENDATION_TYPE.DISABLE_SERVICE,
      RECOMMENDATION_TYPE.REASSIGN,
      RECOMMENDATION_TYPE.REDUCE_PURCHASED,
      RECOMMENDATION_TYPE.REMOVE_REDUNDANT,
      RECOMMENDATION_TYPE.ALERT,
      RECOMMENDATION_TYPE.ALERT_EXCHANGE,
      RECOMMENDATION_TYPE.ALERT_ONEDRIVE,
      RECOMMENDATION_TYPE.ALERT_SHAREPOINT,
      RECOMMENDATION_TYPE.ALERT_TEAMS,
      RECOMMENDATION_TYPE.ALERT_COPILOT,
    ])
    .optional(),
  severity: z
    .enum([
      RECOMMENDATION_SEVERITY.LOW,
      RECOMMENDATION_SEVERITY.MEDIUM,
      RECOMMENDATION_SEVERITY.HIGH,
      RECOMMENDATION_SEVERITY.CRITICAL,
    ])
    .optional(),
  m365TenantId: z.string().optional(),
})

// ─── AI Context ────────────────────────────────────────────

const PROHIBITED_CONTEXT_PATTERNS = [
  /;\s*(DROP|DELETE|INSERT|UPDATE|ALTER|CREATE)\s/i,
  /ignore\s+(all\s+)?previous\s+instructions/i,
  /you\s+are\s+now\s+/i,
  /system\s*:\s*/i,
  /\{\{.*\}\}/,
  /<script/i,
]

function safeContent(val: string): boolean {
  return !PROHIBITED_CONTEXT_PATTERNS.some((p) => p.test(val))
}

export const aiContextCreateSchema = z
  .object({
    scope: z.enum([AI_CONTEXT_SCOPE.GLOBAL, AI_CONTEXT_SCOPE.TENANT, AI_CONTEXT_SCOPE.RECOMMENDATION_TYPE]),
    tenantId: z.string().optional().nullable(),
    recommendationType: z.string().optional().nullable(),
    category: z.enum([AI_CONTEXT_CATEGORY.OPERATIONAL, AI_CONTEXT_CATEGORY.COMMERCIAL, AI_CONTEXT_CATEGORY.OUTPUT_RESTRICTION]),
    title: z.string().min(3).max(200),
    content: z.string().min(10).max(AI_CONTEXT_MAX_LENGTH).refine(safeContent, { message: 'El contenido contiene patrones prohibidos' }),
    isActive: z.boolean().default(true),
    priority: z.number().int().min(0).max(100).default(0),
    visibleToClient: z.boolean().default(false),
    validFrom: z.string().optional().nullable(),
    validUntil: z.string().optional().nullable(),
  })
  .refine(
    (d) => {
      if (d.scope === 'tenant' && !d.tenantId) return false
      if (d.scope === 'recommendation_type' && !d.recommendationType) return false
      return true
    },
    { message: 'Los campos especificos del scope son obligatorios' },
  )
  .refine(
    (d) => {
      if (d.validFrom && d.validUntil) return new Date(d.validFrom) < new Date(d.validUntil)
      return true
    },
    { message: 'validFrom debe ser anterior a validUntil' },
  )

export const aiContextUpdateSchema = z.object({
  title: z.string().min(3).max(200).optional(),
  content: z
    .string()
    .min(10)
    .max(AI_CONTEXT_MAX_LENGTH)
    .refine(safeContent, { message: 'El contenido contiene patrones prohibidos' })
    .optional(),
  category: z.enum([AI_CONTEXT_CATEGORY.OPERATIONAL, AI_CONTEXT_CATEGORY.COMMERCIAL, AI_CONTEXT_CATEGORY.OUTPUT_RESTRICTION]).optional(),
  isActive: z.boolean().optional(),
  priority: z.number().int().min(0).max(100).optional(),
  visibleToClient: z.boolean().optional(),
  validFrom: z.string().optional().nullable(),
  validUntil: z.string().optional().nullable(),
  changeReason: z.string().max(500).optional(),
})

// ─── AI Tenant Config ──────────────────────────────────────

export const aiTenantConfigSchema = z.object({
  featureEnabled: z.boolean().optional(),
  visibilityMode: z.enum([AI_TENANT_VISIBILITY_MODE.ALL, AI_TENANT_VISIBILITY_MODE.APPROVED_ONLY, AI_TENANT_VISIBILITY_MODE.NONE]).optional(),
  autoPublish: z.boolean().optional(),
  strategy: z.enum([AI_TENANT_STRATEGY.AUTOMATIC, AI_TENANT_STRATEGY.CONSERVATIVE, AI_TENANT_STRATEGY.AGGRESSIVE, AI_TENANT_STRATEGY.HIGH_CONFIDENCE_ONLY]).optional(),
  generationFrequency: z.enum([AI_GENERATION_FREQUENCY.DAILY, AI_GENERATION_FREQUENCY.ON_DEMAND, AI_GENERATION_FREQUENCY.ON_SYNC]).optional(),
  categoriesEnabled: z
    .array(z.enum([AI_RECOMMENDATION_CATEGORY.COST_OPTIMIZATION, AI_RECOMMENDATION_CATEGORY.ADOPTION, AI_RECOMMENDATION_CATEGORY.SECURITY, AI_RECOMMENDATION_CATEGORY.COMPLIANCE]))
    .optional(),
  thresholds: z
    .object({
      inactivityDays: z.number().int().min(7).max(365).optional(),
      minUsagePct: z.number().min(0).max(100).optional(),
      minAdoptionPct: z.number().min(0).max(100).optional(),
      minSavingEur: z.number().min(0).optional(),
    })
    .optional(),
  /** Days before a client decision (accepted/applied/dismissed) may be reinvestigated. */
  reinvestigateAfterDays: z.number().int().min(1).max(3650).optional(),
  /** Days an admin rejection suppresses the same hypothesis. */
  adminRejectCooldownDays: z.number().int().min(0).max(3650).optional(),
  /** Investigation run sizing. */
  maxCandidatesPerRun: z.number().int().min(1).max(5000).optional(),
  batchSize: z.number().int().min(1).max(100).optional(),
  /** Research (Agents API) limits and budget. */
  maxInvestigationsPerRun: z.number().int().min(1).max(200).optional(),
  maxMinutesPerInvestigation: z.number().int().min(1).max(60).optional(),
  maxTokensPerRun: z.number().int().min(10_000).max(100_000_000).optional(),
  monthlyTokenBudget: z.number().int().min(10_000).max(1_000_000_000).optional(),
  maxToolCallsPerInvestigation: z.number().int().min(1).max(50).optional(),
})

// ─── AI Agent (OpenAI Agents API) ──────────────────────────

/** Saved agent ids look like `agent_<hex>`; we never create agents, only bind existing ones. */
export const aiAgentConfigSchema = z.object({
  apiKey: z.string().min(20).optional(),
  model: z.string().min(1).max(100).optional(),
  agentId: z.string().regex(/^agent_[A-Za-z0-9]{8,}$/, 'agentId debe tener el formato agent_...').optional().nullable(),
  priceInputPerM: z.number().min(0).max(10_000).optional().nullable(),
  priceOutputPerM: z.number().min(0).max(10_000).optional().nullable(),
})

export const aiResearchFiltersSchema = z.object({
  category: z.string().optional(),
  findingKind: z.enum(['new', 'extension', 'confirmation', 'data_gap', 'legacy']).optional(),
  savingStatus: z.enum(['computed', 'estimated_catalog', 'pending_valuation', 'not_applicable']).optional(),
  status: z.enum(['open', 'reviewed', 'accepted', 'applied', 'dismissed', 'all']).optional(),
  relation: z.enum(['with_rule', 'without_rule']).optional(),
  minConfidence: z.coerce.number().min(0).max(1).optional(),
  minSaving: z.coerce.number().min(0).optional(),
  evidence: z.enum(['weak', 'solid']).optional(),
  m365TenantId: z.string().optional(),
})

export const aiFindingReviewSchema = z.object({
  note: z.string().max(2000).optional().nullable(),
})

export const aiFindingChatMessageSchema = z.object({
  text: z.string().trim().min(1).max(2000),
})

export const aiResearchInvestigateSchema = z.object({
  taskKey: z.string().min(3).max(300),
})

// ─── AI Recommendation Admin ───────────────────────────────

export const aiRecommendationEditSchema = z.object({
  clientTitle: z.string().max(200).optional().nullable(),
  clientDescription: z.string().max(2000).optional().nullable(),
  adminNotes: z.string().max(5000).optional().nullable(),
  severity: z.enum(['low', 'medium', 'high', 'critical']).optional(),
  priority: z.number().int().min(1).max(5).optional(),
  visibility: z.enum([AI_RECOMMENDATION_VISIBILITY.PUBLIC, AI_RECOMMENDATION_VISIBILITY.INTERNAL_ONLY, AI_RECOMMENDATION_VISIBILITY.HIDDEN]).optional(),
  estimatedMonthlySaving: z.number().min(0).optional(),
})

export const aiModerationActionSchema = z.object({
  editNotes: z.string().max(1000).optional(),
  clientTitle: z.string().max(200).optional(),
  clientDescription: z.string().max(2000).optional(),
})

export const aiRecommendationFiltersSchema = z.object({
  status: z.string().optional(),
  category: z.string().optional(),
  origin: z.string().optional(),
  severity: z.string().optional(),
  visibility: z.string().optional(),
  tenantId: z.string().optional(),
  m365TenantId: z.string().optional(),
  minSaving: z.coerce.number().optional(),
  minConfidence: z.coerce.number().optional(),
  search: z.string().optional(),
})

// ─── Type exports ───────────────────────────────────────────

export type LoginInput = z.infer<typeof loginSchema>
export type RegisterInput = z.infer<typeof registerSchema>
export type MfaVerifyInput = z.infer<typeof mfaVerifySchema>
export type InviteUserInput = z.infer<typeof inviteUserSchema>
export type UpdateTenantInput = z.infer<typeof updateTenantSchema>
export type UpdateM365TenantInput = z.infer<typeof updateM365TenantSchema>
export type PaginationInput = z.infer<typeof paginationSchema>
export type UserFiltersInput = z.infer<typeof userFiltersSchema>
export type RecommendationFiltersInput = z.infer<typeof recommendationFiltersSchema>
export type AiContextCreateInput = z.infer<typeof aiContextCreateSchema>
export type AiContextUpdateInput = z.infer<typeof aiContextUpdateSchema>
export type AiTenantConfigInput = z.infer<typeof aiTenantConfigSchema>
export type AiRecommendationEditInput = z.infer<typeof aiRecommendationEditSchema>
export type AiModerationActionInput = z.infer<typeof aiModerationActionSchema>
export type AiRecommendationFiltersInput = z.infer<typeof aiRecommendationFiltersSchema>
